/**
 * What the partner report PDF can hold, as the stats page offers it: a section per heading on the
 * page, and inside the stat-card sections a sub-section per card. Each card is chosen on its own,
 * and the section's checkbox chooses or clears all of them at once.
 *
 * A section with `items` is selected through those items; one without is selected as itself. So
 * the selection is one flat set of keys, and a section is in the report when any of its keys is.
 *
 * `caption` is the card's short line in the PDF. The page keeps its longer explanations; the report
 * follows the template, where a figure carries a few words, not a sentence.
 */
export const REPORT_SECTIONS = [
    {
        id: "users-actions",
        label: "Users & Actions",
        items: [
            { id: "total-users", label: "Total users", caption: "Members as of period end" },
            { id: "active-users", label: "Active users", caption: "At least one action" },
            { id: "active-user-rate", label: "Active user rate", caption: "Of total users" },
            { id: "total-actions", label: "Total actions", caption: "Across all users" },
            { id: "avg-actions-per-user", label: "Avg actions per user", caption: "Per active user" },
        ],
    },
    {
        id: "email-newsletter",
        label: "Email Newsletter",
        items: [
            { id: "email-reach-rate", label: "Email reach rate", caption: "Opened at least one email" },
            { id: "email-click-rate", label: "Email click rate", caption: "Clicked at least one link" },
        ],
    },
    {
        id: "coaching",
        label: "Coaching",
        items: [
            // The template's tile order, sessions last on the dark tile — not the page's card order.
            { id: "coaching-users", label: "Coaching users", caption: "Messaged their coach" },
            { id: "coaching-messages", label: "Coaching messages", caption: "Sent, both sides" },
            { id: "messages-to-coach", label: "Messages to coach", caption: "Sent by users" },
            { id: "coaching-sessions", label: "Coaching sessions", caption: "Continuous conversations" },
        ],
    },
    {
        id: "lms",
        label: "Learning Management System",
        items: [
            { id: "users-in-progress", label: "Users in progress", caption: "Worked on a course" },
            { id: "users-started-course", label: "Users who started a course", caption: "Began a course" },
            { id: "users-completed-course", label: "Users who completed a course", caption: "Finished a course" },
        ],
    },
    { id: "top-video-tags", label: "Top Video Tags" },
    { id: "top-video-topics", label: "Top Video Topics" },
    { id: "platforms-actions", label: "Platforms - Actions" },
    { id: "platforms-users", label: "Platforms - Users" },
    { id: "frequency-tiers", label: "Frequency Tiers" },
    { id: "engagement-buckets", label: "Engagement Buckets" },
    { id: "actions-per-month", label: "Actions per Month" },
    { id: "actions-per-month-by-user", label: "Actions per Month by User" },
    { id: "daily-actions", label: "Daily Actions" },
    { id: "user-growth", label: "User Growth" },
] as const;

type ReportSection = (typeof REPORT_SECTIONS)[number];
type SectionWithItems = Extract<ReportSection, { items: readonly unknown[] }>;

export type ReportSectionId = ReportSection["id"];
export type ReportItem = SectionWithItems["items"][number];
export type ReportItemId = ReportItem["id"];
/** One checkbox's worth of selection: an item, or a section that has no items. */
export type ReportKey = ReportItemId | Exclude<ReportSectionId, SectionWithItems["id"]>;
export type ReportSelection = ReadonlySet<ReportKey>;

/** A checkbox's state: some but not all of a section's items selected reads as "indeterminate". */
export type CheckState = boolean | "indeterminate";

export const reportSection = (id: ReportSectionId): ReportSection =>
    REPORT_SECTIONS.find((section) => section.id === id) as ReportSection;

/** The keys a section is selected through: its items, or itself when it has none. */
export const sectionKeys = (section: ReportSection): ReportKey[] =>
    "items" in section ? section.items.map((item) => item.id) : [section.id];

export const ALL_REPORT_KEYS: readonly ReportKey[] = REPORT_SECTIONS.flatMap(sectionKeys);

/** Checked when every key is selected, clear when none is, and a dash between. */
export const sectionCheckState = (section: ReportSection, selection: ReportSelection): CheckState => {
    const keys = sectionKeys(section);
    const selected = keys.filter((key) => selection.has(key)).length;
    if (selected === 0) return false;
    return selected === keys.length ? true : "indeterminate";
};

export const isSectionIncluded = (id: ReportSectionId, selection: ReportSelection): boolean =>
    sectionKeys(reportSection(id)).some((key) => selection.has(key));

export const isItemIncluded = (id: ReportItemId, selection: ReportSelection): boolean => selection.has(id);

/** Sections with at least one key selected — the count on the export button. */
export const includedSectionCount = (selection: ReportSelection): number =>
    REPORT_SECTIONS.filter((section) => isSectionIncluded(section.id, selection)).length;

export const withKeys = (selection: ReportSelection, keys: readonly ReportKey[], checked: boolean): Set<ReportKey> => {
    const next = new Set(selection);
    for (const key of keys) {
        if (checked) next.add(key);
        else next.delete(key);
    }
    return next;
};

/** The items of `id` that are selected, in the section's order. */
export const selectedItems = (id: SectionWithItems["id"], selection: ReportSelection): ReportItem[] =>
    (reportSection(id) as SectionWithItems).items.filter((item) => selection.has(item.id));
